import { useEffect, useState } from 'react';
import type { EventSummary } from '@loctopus/ticketing-sdk';
import type { ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { useAppContext } from '../lib/AppContext';
import { useRouter } from '../router';
import { Card, EmptyState, Spinner } from '../components/ui';
import { toParsedError } from '../lib/errorDisplay';
import { formatDateRange, formatMoney, isFreeMoney } from '../lib/format';

export function EventListPage() {
  const { client, config } = useAppContext();
  const { navigate } = useRouter();
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error'; error: ParsedApiError }
    | { status: 'ready'; events: EventSummary[] }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    client
      .listEvents({ limit: 50 })
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', events: res.data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', error: toParsedError(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return (
    <div>
      <header className="mb-6">
        {config.theme.logoUrl && (
          <img src={config.theme.logoUrl} alt={config.theme.displayName} className="h-10 mb-3" />
        )}
        <h1 className="font-heading text-2xl font-semibold text-slate-800">
          {config.theme.displayName}
        </h1>
      </header>

      {state.status === 'loading' && <Spinner label="Loading events…" />}
      {state.status === 'error' && <ErrorSummary error={state.error} />}
      {state.status === 'ready' && state.events.length === 0 && (
        <EmptyState title="No events on sale right now" description="Check back soon." />
      )}
      {state.status === 'ready' && (
        <ul className="space-y-3">
          {state.events.map((event) => (
            <li key={event.id}>
              <button
                type="button"
                onClick={() => navigate({ name: 'event', eventId: event.id })}
                className="w-full text-left"
              >
                <Card className="hover:shadow-md transition cursor-pointer">
                  <div className="flex items-center gap-4">
                    {event.coverImageUrl && (
                      <img
                        src={event.coverImageUrl}
                        alt=""
                        className="h-16 w-16 rounded-brand object-cover shrink-0"
                      />
                    )}
                    <div>
                      <h2 className="font-heading font-semibold text-slate-800">{event.title}</h2>
                      <p className="text-sm text-slate-500">
                        {formatDateRange(event.startsAt, event.endsAt, event.timezone)}
                      </p>
                      {event.venueName && (
                        <p className="text-sm text-slate-500">{event.venueName}</p>
                      )}
                      {event.minPrice && (
                        <p className="text-sm font-medium text-brand-primary mt-1">
                          {isFreeMoney(event.minPrice)
                            ? 'Free'
                            : `From ${formatMoney(event.minPrice)}`}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
